import { useEffect, useState, type ReactNode } from 'react';
import './MessengerComposerPreviews.css';
import { CloseIcon } from '@/components/ui/Icons';
import { AttachmentCard } from '@/components/ui/AttachmentCard/AttachmentCard';
import {
  type DirectComposerPendingFile,
  directComposerPendingShowsThumbnail,
} from '@/components/Messenger/types/chatMessageTypes';

function pendingItemIsImage(item: DirectComposerPendingFile): boolean {
  return Boolean(item.file.type?.startsWith('image/'));
}

export interface MessengerComposerPendingPreviewsProps {
  items: readonly DirectComposerPendingFile[];
  onRemove: (index: number) => void;
}

function formatBytes(bytes: number, decimals = 2) {
  if (!+bytes) return '0 Bytes';
  const k = 1024;
  const dm = decimals < 0 ? 0 : decimals;
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(dm))} ${sizes[i]}`;
}

/** Превью в композере: миниатюра только для «фото» / вставка со сжатием; бейдж для «файл» и вложения из модалки. */
export function MessengerComposerPendingPreviews({
  items,
  onRemove,
}: MessengerComposerPendingPreviewsProps): ReactNode {
  const [thumbUrls, setThumbUrls] = useState<(string | null)[]>([]);

  useEffect(() => {
    if (items.length === 0) {
      setThumbUrls([]);
      return;
    }
    const urls = items.map((item) =>
      directComposerPendingShowsThumbnail(item) ? URL.createObjectURL(item.file) : null
    );
    setThumbUrls(urls);
    return () => urls.forEach((u) => u && URL.revokeObjectURL(u));
  }, [items]);

  if (items.length === 0) return null;

  return (
    <div className="chatAssistant__filePreviews">
      {items.map((item, i) => {
        const url = thumbUrls[i];
        if (url) {
          return (
            <div
              key={`${i}-${item.file.name}-${item.file.size}`}
              className="chatAssistant__filePreview"
            >
              <img src={url} alt="" draggable={false} />
              <button
                type="button"
                className="chatAssistant__filePreviewRemove"
                onClick={() => onRemove(i)}
                aria-label="Убрать фото"
              >
                <CloseIcon size={14} />
              </button>
            </div>
          );
        }
        return (
          <AttachmentCard
            key={`${i}-${item.file.name}-${item.file.size}`}
            variant="file"
            title={pendingItemIsImage(item) ? 'Изображение' : item.file.name || 'Файл'}
            subtitle={formatBytes(item.file.size)}
            onRemove={() => onRemove(i)}
            removeButtonClassName="chatAssistant__close"
          />
        );
      })}
    </div>
  );
}
